<template>
  <AdminLayout>
    <div class="p-6 lg:p-8">
      <!-- 페이지 헤더 -->
      <div class="flex items-center gap-3 mb-6">
        <div class="w-10 h-10 bg-blue-600/10 rounded-xl flex items-center justify-center">
          <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"/>
          </svg>
        </div>
        <div>
          <h1 class="text-2xl font-bold text-slate-800">추천 현장 광고 관리</h1>
          <p class="text-sm text-slate-500">"이런 현장 어때요?" 섹션 광고 승인/거절</p>
        </div>
      </div>

      <!-- 필터 탭 -->
      <div class="flex gap-2 mb-6">
        <button
          v-for="tab in tabs"
          :key="tab.value"
          @click="currentTab = tab.value"
          :class="[
            'px-4 py-2 rounded-lg font-medium transition',
            currentTab === tab.value
              ? 'bg-blue-600 text-white shadow-md shadow-blue-600/25'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          ]"
        >
          {{ tab.label }}
          <span v-if="tab.value === 'pending' && pendingCount > 0" class="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
            {{ pendingCount }}
          </span>
        </button>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="flex items-center justify-center py-20">
        <div class="w-12 h-12 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin"></div>
      </div>

      <!-- 빈 상태 -->
      <div v-else-if="filteredRecommendAds.length === 0" class="bg-white rounded-2xl p-12 text-center">
        <svg class="w-16 h-16 mx-auto text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        <h3 class="text-lg font-semibold text-slate-700 mb-2">
          {{ currentTab === 'pending' ? '대기 중인 신청이 없습니다' : '신청 내역이 없습니다' }}
        </h3>
      </div>

      <!-- 신청 목록 -->
      <div v-else class="space-y-4">
        <div
          v-for="ad in filteredRecommendAds"
          :key="ad.id"
          class="bg-white rounded-2xl overflow-hidden shadow-sm"
        >
          <!-- 광고 미리보기 -->
          <div class="relative" style="height: 180px;">
            <div
              class="absolute inset-0"
              :style="{ backgroundImage: `linear-gradient(105deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 100%), url('${ad.imageUrl}')`, backgroundSize: 'cover', backgroundPosition: 'center' }"
            ></div>
            <div class="relative h-full p-5 flex flex-col justify-between text-white">
              <div class="px-2 py-1 text-xs font-bold rounded-md bg-orange-500/90 backdrop-blur-sm self-start">
                AD
              </div>
              <div>
                <h3 class="text-lg font-bold">{{ ad.propertyName }}</h3>
                <p class="text-sm opacity-90 mt-1">{{ ad.title }}</p>
              </div>
            </div>
            <!-- 상태 배지 -->
            <div class="absolute top-3 right-3">
              <span :class="[
                'px-3 py-1 text-xs font-medium rounded-full',
                getStatusClass(ad.status)
              ]">
                {{ getStatusLabel(ad.status) }}
              </span>
            </div>
          </div>

          <!-- 광고 정보 -->
          <div class="p-4">
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p class="text-slate-400">신청자</p>
                <p class="font-medium text-slate-700">{{ ad.partnerDisplayName }}</p>
              </div>
              <div>
                <p class="text-slate-400">현장</p>
                <p class="font-medium text-slate-700">{{ ad.propertyName }}</p>
              </div>
              <div>
                <p class="text-slate-400">캠페인 코드</p>
                <p class="font-medium text-slate-700">{{ ad.campaignCode }}</p>
              </div>
              <div>
                <p class="text-slate-400">노출 시간대</p>
                <p class="font-medium text-slate-700">
                  {{ ad.timeSlot?.icon }} {{ ad.timeSlot?.label || '-' }}
                  <span v-if="ad.timeSlot" class="text-slate-500">({{ ad.timeSlot.start }}-{{ ad.timeSlot.end }})</span>
                </p>
              </div>
              <div>
                <p class="text-slate-400">신청일</p>
                <p class="font-medium text-slate-700">{{ formatDate(ad.createdAt) }}</p>
              </div>
              <div>
                <p class="text-slate-400">기간</p>
                <p class="font-medium text-slate-700">1주일</p>
              </div>
              <div v-if="ad.status === 'approved'">
                <p class="text-slate-400">노출/클릭</p>
                <p class="font-medium text-slate-700">{{ ad.impressions || 0 }} / {{ ad.clicks || 0 }}</p>
              </div>
            </div>

            <!-- 거절 사유 (거절된 경우) -->
            <div v-if="ad.status === 'rejected' && ad.rejectReason" class="mt-4 p-3 bg-red-50 rounded-xl">
              <p class="text-sm text-red-600">
                <strong>거절 사유:</strong> {{ ad.rejectReason }}
              </p>
            </div>

            <!-- 승인/거절 버튼 (대기 중인 경우) -->
            <div v-if="ad.status === 'pending'" class="mt-4 pt-4 border-t border-slate-100 flex gap-3">
              <button
                @click="handleApprove(ad)"
                :disabled="processing === ad.id"
                class="flex-1 py-2.5 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition disabled:opacity-50"
              >
                {{ processing === ad.id ? '처리 중...' : '승인' }}
              </button>
              <button
                @click="openRejectModal(ad)"
                :disabled="processing === ad.id"
                class="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                거절
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 거절 사유 모달 -->
      <div v-if="showRejectModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-2xl p-6 w-full max-w-md">
          <h3 class="text-lg font-semibold text-slate-800 mb-4">거절 사유 입력</h3>
          <textarea
            v-model="rejectReason"
            placeholder="거절 사유를 입력해주세요..."
            rows="4"
            class="w-full px-4 py-3 border border-slate-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-500/20 outline-none resize-none"
          ></textarea>
          <div class="flex gap-3 mt-4">
            <button
              @click="showRejectModal = false"
              class="flex-1 py-2.5 bg-slate-100 text-slate-600 font-medium rounded-lg hover:bg-slate-200 transition"
            >
              취소
            </button>
            <button
              @click="handleReject"
              :disabled="!rejectReason.trim() || processing"
              class="flex-1 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition disabled:opacity-50"
            >
              {{ processing ? '처리 중...' : '거절하기' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import AdminLayout from '@/components/admin/common/AdminLayout.vue'
import { useRecommendAd } from '@/composables/partners/useRecommendAd'

export default {
  name: 'RecommendAdApplicationsView',
  components: {
    AdminLayout
  },
  setup() {
    const { getAllRecommendAds, updateRecommendAdStatus } = useRecommendAd()

    const loading = ref(true)
    const recommendAds = ref([])
    const currentTab = ref('pending')
    const processing = ref(null)
    const showRejectModal = ref(false)
    const rejectReason = ref('')
    const selectedAd = ref(null)

    const tabs = [
      { label: '대기', value: 'pending' },
      { label: '승인', value: 'approved' },
      { label: '거절', value: 'rejected' },
      { label: '전체', value: 'all' }
    ]

    const filteredRecommendAds = computed(() => {
      if (currentTab.value === 'all') return recommendAds.value
      return recommendAds.value.filter(ad => ad.status === currentTab.value)
    })

    const pendingCount = computed(() => {
      return recommendAds.value.filter(ad => ad.status === 'pending').length
    })

    const loadRecommendAds = async () => {
      loading.value = true
      try {
        recommendAds.value = await getAllRecommendAds()
      } catch (err) {
        console.error('추천 광고 목록 로드 실패:', err)
      } finally {
        loading.value = false
      }
    }

    const getStatusLabel = (status) => {
      switch (status) {
        case 'pending': return '승인 대기'
        case 'approved': return '승인됨'
        case 'rejected': return '거절됨'
        default: return status
      }
    }

    const getStatusClass = (status) => {
      switch (status) {
        case 'pending': return 'bg-amber-100 text-amber-700'
        case 'approved': return 'bg-green-100 text-green-700'
        case 'rejected': return 'bg-red-100 text-red-600'
        default: return 'bg-slate-100 text-slate-600'
      }
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    }

    const handleApprove = async (ad) => {
      if (!confirm(`${ad.partnerDisplayName}의 추천 광고를 승인하시겠습니까?`)) return

      processing.value = ad.id
      try {
        await updateRecommendAdStatus(ad.id, 'approved')
        alert('승인되었습니다.')
        await loadRecommendAds()
      } catch (err) {
        console.error('승인 실패:', err)
        alert('승인에 실패했습니다: ' + err.message)
      } finally {
        processing.value = null
      }
    }

    const openRejectModal = (ad) => {
      selectedAd.value = ad
      rejectReason.value = ''
      showRejectModal.value = true
    }

    const handleReject = async () => {
      if (!selectedAd.value || !rejectReason.value.trim()) return

      processing.value = selectedAd.value.id
      try {
        await updateRecommendAdStatus(selectedAd.value.id, 'rejected', rejectReason.value.trim())
        alert('거절되었습니다.')
        showRejectModal.value = false
        await loadRecommendAds()
      } catch (err) {
        console.error('거절 실패:', err)
        alert('거절에 실패했습니다: ' + err.message)
      } finally {
        processing.value = null
      }
    }

    onMounted(() => {
      loadRecommendAds()
    })

    return {
      loading,
      recommendAds,
      currentTab,
      tabs,
      filteredRecommendAds,
      pendingCount,
      processing,
      showRejectModal,
      rejectReason,
      getStatusLabel,
      getStatusClass,
      formatDate,
      handleApprove,
      openRejectModal,
      handleReject
    }
  }
}
</script>

<style scoped>
@reference "tailwindcss";
</style>
